import { useCallback, useEffect, useRef, useState } from 'react';

import type { Employee } from 'types/interfaces';
import {
  isPlanningAlgorithmError,
  PLANNING_ALGORITHM_ERROR_MESSAGE
} from 'utils/taskCard/taskAssignmentErrors';
import type { EmployeeAssignmentRulesParams } from 'utils/taskEmployeeAssignmentRules';

import { EmployeesService } from 'components/services';

import { buildDeptCacheKey } from '../lib/distributionHelpers';

export function useDeptEmployeesCache(visible: boolean) {
  const [employeesByKey, setEmployeesByKey] = useState<
    Record<string, Employee[]>
  >({});
  const [inFlight, setInFlight] = useState<Record<string, true>>({});
  const [errorsByKey, setErrorsByKey] = useState<Record<string, string>>({});
  const [showAllByTaskId, setShowAllByTaskId] = useState<
    Record<number, boolean>
  >({});
  const employeesByKeyRef = useRef<Record<string, Employee[]>>({});
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!visible) return;
    employeesByKeyRef.current = {};
    setEmployeesByKey({});
    setErrorsByKey({});
    inFlightRef.current.clear();
    setInFlight({});
    setShowAllByTaskId({});
  }, [visible]);

  const ensureEmployees = useCallback(
    (
      deptId: number,
      showAll: boolean,
      assignmentRulesParams?: EmployeeAssignmentRulesParams | null
    ) => {
      const key = buildDeptCacheKey(deptId, showAll, assignmentRulesParams);

      const loadEmployees = (
        cacheKey: string,
        requestFactory: () => ReturnType<
          typeof EmployeesService.getByDepartments
        >
      ) => {
        if (employeesByKeyRef.current[cacheKey] !== undefined) return;
        if (inFlightRef.current.has(cacheKey)) return;

        inFlightRef.current.add(cacheKey);
        setInFlight((prev) => ({ ...prev, [cacheKey]: true }));

        requestFactory()
          .then((resp) => {
            if (resp.status === 400 && isPlanningAlgorithmError(resp.data)) {
              setErrorsByKey((prev) => ({
                ...prev,
                [cacheKey]: PLANNING_ALGORITHM_ERROR_MESSAGE
              }));
              employeesByKeyRef.current = {
                ...employeesByKeyRef.current,
                [cacheKey]: []
              };
              setEmployeesByKey(employeesByKeyRef.current);
              return;
            }
            setErrorsByKey((prev) => {
              const next = { ...prev };
              delete next[cacheKey];
              return next;
            });
            const list = resp?.data?.value;
            const mapped: Employee[] = Array.isArray(list)
              ? list.map((x: any) => ({
                ...x,
                key: x.id?.toString?.() ?? '',
                id: x.id,
                label: x.fullName?.toString?.() ?? x.label ?? ''
              }))
              : [];
            employeesByKeyRef.current = {
              ...employeesByKeyRef.current,
              [cacheKey]: mapped
            };
            setEmployeesByKey(employeesByKeyRef.current);
          })
          .catch(() => {
            setErrorsByKey((prev) => {
              const next = { ...prev };
              delete next[cacheKey];
              return next;
            });
            employeesByKeyRef.current = {
              ...employeesByKeyRef.current,
              [cacheKey]: []
            };
            setEmployeesByKey(employeesByKeyRef.current);
          })
          .finally(() => {
            inFlightRef.current.delete(cacheKey);
            setInFlight((prev) => {
              const next = { ...prev };
              delete next[cacheKey];
              return next;
            });
          });
      };

      const startDate = assignmentRulesParams?.start;
      const endDate = assignmentRulesParams?.end;
      if (!showAll) {
        loadEmployees(buildDeptCacheKey(deptId, true), () =>
          EmployeesService.getByDepartments(
            deptId,
            undefined,  
            false,      
            undefined, 
            startDate,  
            endDate
          )
        );
      }

      loadEmployees(key, () =>
        showAll
          ? EmployeesService.getByDepartments(
            deptId,
            undefined,  
            false,      
            undefined, 
            startDate,  
            endDate     
          )
          : EmployeesService.getByDepartmentAssignmentRules(
            assignmentRulesParams ?? undefined
          )
      );
    },
    []
  );

  const setShowAllForTask = useCallback((taskId: number, val: boolean) => {
    setShowAllByTaskId((prev) => ({ ...prev, [taskId]: val }));
  }, []);

  return {
    employeesByKey,
    inFlight,
    errorsByKey,
    ensureEmployees,
    showAllByTaskId,
    setShowAllForTask
  };
}
